import { AlertTriangle } from 'lucide-react'
import { Invoice } from '@/types'
import { formatCurrency } from '@/lib/utils'
import { cn } from '@lib/utils'

interface Props {
  invoices: Invoice[]
}

export function ShiftBillingKPIs({ invoices }: Props) {
  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0)
  const totalPaid = invoices.reduce((sum, inv) => sum + Number(inv.amount_paid || 0), 0)
  const outstanding = invoices.reduce((sum, inv) => sum + Number(inv.balance_due || 0), 0)
  const unpaidCount = invoices.filter((inv) => Number(inv.balance_due || 0) > 0).length

  const overdue = invoices.filter((inv) => inv.status === 'overdue')
  const overdueCount = overdue.length
  const overdueAmount = overdue.reduce((sum, inv) => sum + Number(inv.balance_due || 0), 0)

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {/* Total Facturado */}
      <div className="bg-ml-surface-container-lowest rounded-xl p-5 flex flex-col shadow-[0_2px_8px_rgba(3,36,26,0.03)]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40">
          Total Facturado
        </p>
        <p className="text-2xl font-bold text-ml-primary tracking-tight mt-2">
          {formatCurrency(totalBilled)}
        </p>
        <p className="text-[11px] text-ml-on-surface-variant/50 mt-auto pt-3">
          {invoices.length} {invoices.length === 1 ? 'factura' : 'facturas'} en el periodo
        </p>
      </div>

      {/* Total Cobrado */}
      <div className="bg-ml-surface-container-lowest rounded-xl p-5 flex flex-col shadow-[0_2px_8px_rgba(3,36,26,0.03)]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40">
          Total Cobrado
        </p>
        <p className="text-2xl font-bold text-ml-primary tracking-tight mt-2">
          {formatCurrency(totalPaid)}
        </p>
        <p className="text-[11px] text-ml-on-surface-variant/50 mt-auto pt-3">
          de {formatCurrency(totalBilled)} facturado
        </p>
      </div>

      {/* Saldo Pendiente */}
      <div className="bg-ml-surface-container-lowest rounded-xl p-5 flex flex-col shadow-[0_2px_8px_rgba(3,36,26,0.03)]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40">
          Saldo Pendiente
        </p>
        <p className="text-2xl font-bold text-ml-primary tracking-tight mt-2">
          {formatCurrency(outstanding)}
        </p>
        <p className="text-[11px] text-ml-on-surface-variant/50 mt-auto pt-3">
          {unpaidCount} {unpaidCount === 1 ? 'factura' : 'facturas'} por cobrar
        </p>
      </div>

      {/* Facturas Vencidas */}
      <div
        className={cn(
          'rounded-xl p-5 flex flex-col shadow-[0_2px_8px_rgba(3,36,26,0.03)]',
          overdueCount > 0
            ? 'bg-red-50/60 border border-red-200/60'
            : 'bg-ml-surface-container-lowest',
        )}
      >
        <div className="flex items-center justify-between">
          <p
            className={cn(
              'text-[10px] font-bold uppercase tracking-widest',
              overdueCount > 0 ? 'text-red-500/70' : 'text-ml-on-surface-variant/40',
            )}
          >
            Facturas Vencidas
          </p>
          {overdueCount > 0 && <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
        </div>
        <p
          className={cn(
            'text-2xl font-bold tracking-tight mt-2',
            overdueCount > 0 ? 'text-red-600' : 'text-ml-primary',
          )}
        >
          {overdueCount > 0 ? formatCurrency(overdueAmount) : '—'}
        </p>
        <p
          className={cn(
            'text-[11px] mt-auto pt-3',
            overdueCount > 0 ? 'text-red-500/70' : 'text-ml-on-surface-variant/50',
          )}
        >
          {overdueCount > 0
            ? `${overdueCount} ${overdueCount === 1 ? 'factura vencida' : 'facturas vencidas'}`
            : 'Sin facturas vencidas'}
        </p>
      </div>
    </div>
  )
}
